import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { CLIENT_DETAILS_QUERY_KEY, CLIENT_QUERY_KEY } from '@/app/(main)/client-detail/consts/client-detail';
import { CLIENT_GROUPED_QUERY_KEY } from '@/app/(main)/client-detail/hooks/useGetClientGroupedQuery';
import { CLIENT_CREATE_QUERY_KEY, RELATED_TRAVELERS_QUERY_KEY } from '@/app/(main)/clients/create/const/const';
import { useEditClientMutation } from '@/app/(main)/clients/hooks/useEditClientMutation';
import { additionalPhones } from '@/app/(main)/client-detail/utils/contacts';
import { type CreateClientFormData, type EditClientData } from '@/types/clientsApiTypes';
import { EditBasicInformationForm } from '../../EditSectionModals/components/EditBasicInformationForm';
import { ModalKeys } from '../../EditSectionModals/types';
import { defineSection } from '../../EditSectionModals/utils/defineSection';
import { getRelatedBasicInfoPayload } from '../../EditSectionModals/utils/getRelatedBasicInfoPayload';
import { mapRelatedBasicInfoFormValues } from '../../EditSectionModals/utils/mapRelatedBasicInfoFormValues';
import { relatedBasicInfoSchema } from './schema';

const BasicInfoFormSection = () => {
  return <EditBasicInformationForm isPrimaryPassenger={false} />;
};

function useBasicInfoOnSave() {
  const mutation = useEditClientMutation();
  const queryClient = useQueryClient();

  return async (payload: Partial<EditClientData>) => {
    const { id, ...clientRest } = payload.client ?? {};
    await mutation.mutateAsync(
      {
        data: {
          client: clientRest,
        },
        clientId: Number(id),
      },
      {
        onSuccess: () => {
          toast.success('Client updated successfully');
          queryClient.invalidateQueries({ queryKey: [CLIENT_CREATE_QUERY_KEY] });
          queryClient.invalidateQueries({ queryKey: [RELATED_TRAVELERS_QUERY_KEY] });
          queryClient.invalidateQueries({ queryKey: [CLIENT_QUERY_KEY] });
          queryClient.invalidateQueries({ queryKey: [CLIENT_DETAILS_QUERY_KEY] });
          queryClient.invalidateQueries({ queryKey: [CLIENT_GROUPED_QUERY_KEY] });
        },
      }
    );
  };
}

export const relatedBasicInfoSection = defineSection<
  (typeof ModalKeys)['related_basic_info'],
  CreateClientFormData,
  typeof relatedBasicInfoSchema,
  Partial<EditClientData>
>({
  key: ModalKeys.related_basic_info,
  title: 'Basic information',
  subtitle: 'Passenger details used for identification and communication.',
  schema: relatedBasicInfoSchema,
  mapClientToValues: mapRelatedBasicInfoFormValues,
  mapValuesToPayload: (v, client): Partial<EditClientData> => {
    const travelerId = client?.related_passengers?.[0]?.client.client_id ?? 0;
    const travelerClient = v.related_passengers?.[0]?.client;

    if (!travelerClient) {
      return {};
    }

    return {
      client: getRelatedBasicInfoPayload({
        client_id: travelerId,
        userHasCertifiedTheInformation: travelerClient.userHasCertifiedTheInformation ?? false,
        preferred_name: travelerClient.preferred_name ?? null,
        relationship: travelerClient.relationship ?? null,
        first_name: travelerClient.first_name,
        middle_name: travelerClient.middle_name ?? null,
        last_name: travelerClient.last_name,
        gender: travelerClient.gender ?? null,
        email: travelerClient.email ?? null,
        additional_emails: travelerClient.additional_emails ?? [],
        phones: additionalPhones(travelerClient.phones),
        date_of_birth: travelerClient.date_of_birth ?? null,
        phone: travelerClient.phone ?? null,
        crm_emails: client?.related_passengers?.[0]?.client.crm_emails,
        crm_phones: client?.related_passengers?.[0]?.client.crm_phones,
        contact_primary_client_id: travelerClient.contact_primary_client_id ?? null,
        preferred_airport_ids: (
          (travelerClient as { preferred_airport_ids?: (number | null)[] }).preferred_airport_ids ?? []
        ).filter((id): id is number => typeof id === 'number'),
      }),
    };
  },
  useOnSave: useBasicInfoOnSave,
  errorFieldPrefix: 'related_passengers.0',
  Fields: BasicInfoFormSection,
});